/**
 * Authentication Context — Impact Core
 *
 * Manages login, logout, token storage, and user state.
 *
 * Login flow:
 *   1. POST /api/v1/auth/token (OAuth2 form-data) -> access_token + refresh_token
 *   2. GET  /api/v1/auth/me (Bearer token)        -> user profile
 *   3. Map SI Platform role to IC role via mapSIRoleToICRole()
 *
 * Tokens are stored under `ic_*` keys so the main SI Platform session
 * on sandbox.society-intelligence.com is never disturbed.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { apiClient, STORAGE_KEYS } from '@/api/client';
import { env } from '@/config/env';
import { mapSIRoleToICRole } from '@/config/roles';
import type { User, ICRole, LoginResponse } from '@/types';

// ─── Context Shape ─────────────────────────────────────────────

interface AuthContextType {
  user: User | null;
  icRole: ICRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ──────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const icRole: ICRole = user ? mapSIRoleToICRole(user.role) : 'fund_manager';
  const isAuthenticated = user !== null;

  /**
   * Fetch the current user profile using the stored access token.
   */
  const fetchUser = useCallback(async (): Promise<User | null> => {
    const token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (!token) return null;

    try {
      const response = await apiClient.get<User>('/api/v1/auth/me');
      return response.data;
    } catch {
      // Token invalid / expired
      localStorage.removeItem(STORAGE_KEYS.accessToken);
      localStorage.removeItem(STORAGE_KEYS.refreshToken);
      return null;
    }
  }, []);

  /**
   * On mount: attempt to restore session from stored token.
   */
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const restoredUser = await fetchUser();
      if (!cancelled) {
        setUser(restoredUser);
        setIsLoading(false);
      }
    }

    void init();
    return () => { cancelled = true; };
  }, [fetchUser]);

  /**
   * Login with email + password via OAuth2 form-data endpoint.
   */
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    // Build OAuth2 form-data (SI backend expects `username` field)
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const tokenResponse = await axios.post<LoginResponse>(
      `${env.apiBaseUrl}/api/v1/auth/token`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );

    const { access_token, refresh_token } = tokenResponse.data;
    localStorage.setItem(STORAGE_KEYS.accessToken, access_token);
    localStorage.setItem(STORAGE_KEYS.refreshToken, refresh_token);

    // Fetch user profile with the new token
    const userResponse = await apiClient.get<User>('/api/v1/auth/me');
    setUser(userResponse.data);
  }, []);

  /**
   * Clear tokens and redirect to login.
   */
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, icRole, isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ──────────────────────────────────────────────────────

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
